import { Injectable } from '@angular/core';
import {
  Resolve,
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, map, take, filter } from 'rxjs/operators';

import { DealVersion, DealFacade } from '@ipreo/muni-shared';
import {
  ModalOptions,
  ModalService,
  ModalSize,
  ModalType
} from '@ipreo/ngx-sprinkles';
import { DealErrorModalComponent } from '../components/deal-error-modal-component/deal-error-modal-component';

const GAMEDAY_NOT_ENABLED_MESSAGE =
  'Gameday is not enabled for this deal. ' +
  'To enable Gameday, please contact ' +
  '<a href="mailto:munis@spglobal.com">munis@spglobal.com</a>.';

const DEAL_NOT_RELEASED_MESSAGE =
  'This deal is not released yet. ' +
  'You will be able to load Gameday for this deal ' +
  'once it has been released.';

const DEAL_LOAD_FAILURE_MESSAGE =
  'Something went wrong and we are unable to load this deal. ' +
  'Please contact support if the problem persists.';

const MODAL_OPTIONS: ModalOptions = {
  title: "We Couldn't Load Your Deal",
  type: ModalType.View,
  size: ModalSize.Medium,
  view: DealErrorModalComponent
};

@Injectable()
export class DealResolver
  implements Resolve<boolean>, CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private modalService: ModalService,
    private dealFacade: DealFacade
  ) {}

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.resolve(route);
  }

  public canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.resolve(route);
  }

  public resolve(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.getDeal(route.params.id).pipe(
      mergeMap((deal: DealVersion) => {
        if (deal.gamedaySettings.enabled) {
          return of(true);
        } else if (deal.series) {
          return this.showError(GAMEDAY_NOT_ENABLED_MESSAGE);
        } else {
          return this.showError(DEAL_NOT_RELEASED_MESSAGE);
        }
      }),
      catchError((error) => {
        console.log('error: ', error);
        return this.showError(DEAL_LOAD_FAILURE_MESSAGE);
      })
    );
  }

  private showError(message: string) {
    this.showModal(message);
    this.router.navigate(['../']);
    return of(false);
  }

  private getDeal(dealId: string) {
    this.dealFacade.setUseDefaultVersion();
    this.dealFacade.setDealId(dealId);

    return this.dealFacade.dealState$.pipe(
      filter((dealInfo) => dealInfo.loaded),
      map((dealInfo) => dealInfo.deal),
      take(1)
    );
  }

  private showModal(message: string): void {
    this.modalService.create({
      ...MODAL_OPTIONS,
      context: {
        text: message
      }
    });
  }
}
